import React, { useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import {
  ShieldCheck, AlertTriangle, Inbox, MessageSquare, FileText, Send, Phone,
  ClipboardCheck, Wrench, Flame, CheckCircle2, Clock, ChevronDown, ChevronRight,
  Lightbulb, Sparkles, Plus, ExternalLink, Wallet,
} from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'

// ── Reception accountability board ───────────────────────────────────────────
// Paul's "hawk-eye" over the front desk: one accountability score, a red PUSH LIST
// of everything overdue, and a lane per responsibility (email, flagged WhatsApp,
// estimates, referrals, call quality, stock take, equipment servicing).

const ICONS = {
  Inbox, MessageSquare, FileText, Send, Phone, ClipboardCheck, Wrench, AlertTriangle, Wallet,
}

const BAND = {
  green:   { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', ring: 'text-emerald-500', label: 'On track' },
  amber:   { dot: 'bg-amber-500',   text: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200',   ring: 'text-amber-500',   label: 'Slipping' },
  red:     { dot: 'bg-red-500',     text: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-200',     ring: 'text-red-500',     label: 'Overdue' },
  unknown: { dot: 'bg-gray-400',    text: 'text-gray-600',    bg: 'bg-gray-50',    border: 'border-gray-200',    ring: 'text-gray-400',    label: 'No data' },
}

function fmtWhen(iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) }
  catch { return '' }
}

// Circular accountability gauge (SVG, no deps).
function Gauge({ score, band }) {
  const b = BAND[band] || BAND.unknown
  const r = 52, c = 2 * Math.PI * r
  const pct = Math.max(0, Math.min(100, score)) / 100
  return (
    <div className="relative flex h-36 w-36 flex-shrink-0 items-center justify-center">
      <svg className="h-36 w-36 -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} fill="none" stroke="currentColor" strokeWidth="10" className="text-brand-border" />
        <circle cx="60" cy="60" r={r} fill="none" stroke="currentColor" strokeWidth="10" strokeLinecap="round"
          className={b.ring} strokeDasharray={c} strokeDashoffset={c * (1 - pct)} />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className={`text-4xl font-bold ${b.text}`}>{score}</span>
        <span className="text-xs font-medium text-brand-muted">/ 100</span>
      </div>
    </div>
  )
}

function Sparkline({ trend }) {
  if (!trend || trend.length < 2) return <p className="text-xs text-brand-muted">Trend builds over the next few days.</p>
  const w = 220, h = 44, pad = 4
  const xs = trend.map((_, i) => pad + (i * (w - 2 * pad)) / (trend.length - 1))
  const ys = trend.map((t) => h - pad - ((Math.max(0, Math.min(100, t.score)) / 100) * (h - 2 * pad)))
  const d = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ')
  return (
    <svg width={w} height={h} className="overflow-visible">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="2" className="text-brand-primary" />
      {xs.map((x, i) => <circle key={i} cx={x} cy={ys[i]} r="2" className="fill-brand-primary" />)}
    </svg>
  )
}

function StatusPill({ status }) {
  const b = BAND[status] || BAND.unknown
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${b.bg} ${b.text} ${b.border}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${b.dot}`} />{b.label}
    </span>
  )
}

function LaneCard({ lane, onResolve }) {
  const [open, setOpen] = useState(false)
  const b = BAND[lane.status] || BAND.unknown
  const Icon = ICONS[lane.icon] || AlertTriangle
  const items = lane.outstanding || []
  return (
    <div className={`rounded-2xl border bg-white p-5 shadow-sm ${lane.status === 'red' ? 'border-red-200' : 'border-brand-border'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${b.bg}`}>
            <Icon size={18} className={b.text} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-brand-ink">{lane.label}</h3>
            <p className="text-xs text-brand-muted">{lane.target}</p>
          </div>
        </div>
        <StatusPill status={lane.status} />
      </div>

      <p className={`mt-4 text-sm font-medium ${lane.status === 'red' ? 'text-red-700' : 'text-brand-ink'}`}>{lane.headline}</p>
      <p className="mt-1 text-xs text-brand-muted">{lane.value}</p>

      {items.length > 0 && (
        <button onClick={() => setOpen((o) => !o)}
          className="mt-3 flex items-center gap-1 text-xs font-semibold text-brand-primary hover:underline">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {items.length} item{items.length !== 1 ? 's' : ''} to action
        </button>
      )}
      {open && (
        <ul className="mt-2 space-y-1.5">
          {items.map((it, i) => {
            const sb = BAND[it.severity] || BAND.unknown
            return (
              <li key={i} className={`flex items-start justify-between gap-2 rounded-lg border px-3 py-2 ${sb.bg} ${sb.border}`}>
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-brand-ink">
                    {it.urgent && <Flame size={12} className="mr-1 inline text-red-500" />}{it.title}</p>
                  <p className="truncate text-[11px] text-brand-muted">{it.subtitle}</p>
                  {it.elapsed_label && <p className={`text-[11px] font-medium ${sb.text}`}>Waiting {it.elapsed_label}</p>}
                  {it.note && <p className={`text-[11px] font-medium ${sb.text}`}>{it.note}</p>}
                </div>
                <div className="mt-0.5 flex flex-shrink-0 items-center gap-1.5">
                  {it.resolve_url && (
                    <button onClick={() => onResolve && onResolve(it.resolve_url)}
                      className="rounded-md border border-brand-border bg-white px-2 py-0.5 text-[10px] font-semibold text-brand-muted hover:bg-brand-surface">Reviewed</button>
                  )}
                  {it.link && (
                    <a href={it.link} className="text-brand-muted hover:text-brand-primary"><ExternalLink size={13} /></a>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default function Receptionist({ report }) {
  const acc = report?.accountability || { score: 0, band: 'unknown', verdict: '', streak_days: 0, trend: [] }
  const lanes = report?.lanes || []
  const red = report?.red_items || []
  const callLane = lanes.find((l) => l.key === 'call_quality')
  const b = BAND[acc.band] || BAND.unknown
  const [showRefForm, setShowRefForm] = useState(false)
  const [ref, setRef] = useState({ patient_name: '', referred_to: '', reason: '', urgent: false })

  const post = (url, data = {}) => router.post(url, data, { preserveScroll: true })

  const submitRef = (e) => {
    e.preventDefault()
    router.post('/receptionist/referrals', ref, {
      preserveScroll: true,
      onSuccess: () => { setShowRefForm(false); setRef({ patient_name: '', referred_to: '', reason: '', urgent: false }) },
    })
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <ShieldCheck className="text-brand-primary" size={26} />
          <div>
            <h1 className="text-xl font-bold text-brand-ink">Reception accountability</h1>
            <p className="text-sm text-brand-muted">
              One person, one score — is <span className="font-medium">{report?.accountable || 'Reception'}</span> on top of everything?
              {report && <span className="ml-2 text-xs">Updated {fmtWhen(report.generated_at)}</span>}
            </p>
          </div>
        </div>

        {/* Score + streak + trend */}
        <div className={`mb-6 grid gap-5 rounded-2xl border p-6 md:grid-cols-[auto,1fr] ${b.border} ${b.bg}`}>
          <div className="flex items-center gap-5">
            <Gauge score={acc.score} band={acc.band} />
            <div>
              <p className={`text-lg font-bold ${b.text}`}>{acc.verdict}</p>
              <p className="mt-1 text-sm text-brand-muted">{acc.red_lanes > 0 ? `${acc.red_lanes} area${acc.red_lanes !== 1 ? 's' : ''} overdue` : 'No overdue areas'}</p>
              <div className="mt-3 flex items-center gap-2 text-sm">
                <Sparkles size={15} className="text-amber-500" />
                <span className="font-semibold text-brand-ink">{acc.streak_days}</span>
                <span className="text-brand-muted">day green streak</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col justify-center border-t border-brand-border pt-4 md:border-l md:border-t-0 md:pl-6 md:pt-0">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-muted">21-day trend</p>
            <Sparkline trend={acc.trend} />
          </div>
        </div>

        {/* PUSH LIST — the red hawk-eye list */}
        <div className="mb-6 rounded-2xl border border-red-200 bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b border-red-100 bg-red-50 px-5 py-3">
            <Flame className="text-red-500" size={18} />
            <h2 className="text-sm font-bold text-red-700">Push list — overdue right now ({red.length})</h2>
          </div>
          {red.length === 0 ? (
            <div className="flex items-center gap-2 px-5 py-6 text-sm text-emerald-700">
              <CheckCircle2 size={18} /> Nothing overdue. Reception is on top of it.
            </div>
          ) : (
            <ul className="divide-y divide-brand-border">
              {red.map((it, i) => (
                <li key={i} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-brand-ink">
                      {it.urgent && <Flame size={13} className="mr-1 inline text-red-500" />}{it.title}</p>
                    <p className="truncate text-xs text-brand-muted">{it.subtitle}</p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-3">
                    <span className="flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">
                      <Clock size={12} /> {it.elapsed_label}
                    </span>
                    {it.link && <a href={it.link} className="text-brand-muted hover:text-brand-primary"><ExternalLink size={15} /></a>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Lanes */}
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {lanes.map((lane) => <LaneCard key={lane.key} lane={lane} onResolve={(url) => post(url)} />)}
        </div>

        {/* Call quality detail */}
        {callLane && (
          <div className="mb-6 rounded-2xl border border-brand-border bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Phone size={18} className="text-brand-primary" />
                <h2 className="text-sm font-bold text-brand-ink">Conversation quality (SOP) — calls + WhatsApp</h2>
              </div>
              <span className="text-[11px] text-brand-muted">Scored off-API on Paul's laptop</span>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ['Avg SOP score', callLane.stats?.avg_score != null ? `${callLane.stats.avg_score}%` : '—'],
                ['Reviewed', callLane.stats?.reviewed ?? 0],
                ['Low-scoring', callLane.stats?.low_calls ?? 0],
                ['Waitlist added', callLane.stats?.waitlist_added ?? 0],
              ].map(([k, v]) => (
                <div key={k} className="rounded-xl bg-brand-surface px-3 py-2">
                  <p className="text-lg font-bold text-brand-ink">{v}</p>
                  <p className="text-[11px] text-brand-muted">{k}</p>
                </div>
              ))}
            </div>
            {(callLane.stats?.weak_items || []).length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-brand-muted">Most-missed SOP steps:</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {callLane.stats.weak_items.map((w, i) => (
                    <span key={i} className="rounded-full bg-amber-50 px-2.5 py-1 text-xs text-amber-700">{w.name} ({w.fails})</span>
                  ))}
                </div>
              </div>
            )}
            {(callLane.outstanding || []).length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {callLane.outstanding.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-2 rounded-lg border border-brand-border px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold text-brand-ink">{c.title} — <span className={c.score < 80 ? 'text-red-600' : 'text-amber-600'}>{c.score}%</span></p>
                      <p className="truncate text-[11px] text-brand-muted">{c.subtitle}</p>
                    </div>
                    <button onClick={() => post('/receptionist/calls/' + c.id + '/acknowledge')}
                      className="flex-shrink-0 rounded-md border border-brand-border px-2 py-1 text-[11px] font-medium text-brand-muted hover:bg-brand-surface">Reviewed</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Referral logging */}
        <div className="mb-6 rounded-2xl border border-brand-border bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Send size={18} className="text-brand-primary" />
              <h2 className="text-sm font-bold text-brand-ink">Log a referral to track</h2>
            </div>
            <button onClick={() => setShowRefForm((s) => !s)}
              className="flex items-center gap-1 rounded-lg bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white">
              <Plus size={14} /> New referral
            </button>
          </div>
          {showRefForm && (
            <form onSubmit={submitRef} className="mt-4 grid gap-3 sm:grid-cols-2">
              <input required placeholder="Patient name" value={ref.patient_name}
                onChange={(e) => setRef({ ...ref, patient_name: e.target.value })}
                className="rounded-lg border border-brand-border px-3 py-2 text-sm" />
              <input placeholder="Referred to (specialist / practice)" value={ref.referred_to}
                onChange={(e) => setRef({ ...ref, referred_to: e.target.value })}
                className="rounded-lg border border-brand-border px-3 py-2 text-sm" />
              <input placeholder="Reason (optional)" value={ref.reason}
                onChange={(e) => setRef({ ...ref, reason: e.target.value })}
                className="rounded-lg border border-brand-border px-3 py-2 text-sm sm:col-span-2" />
              <label className="flex items-center gap-2 text-sm text-brand-ink">
                <input type="checkbox" checked={ref.urgent} onChange={(e) => setRef({ ...ref, urgent: e.target.checked })} />
                Urgent (suspected pathology — 1 business day SLA)
              </label>
              <div className="sm:col-span-2">
                <button type="submit" className="rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white">Start the clock</button>
              </div>
            </form>
          )}
        </div>

        {/* Recommendations */}
        <div className="rounded-2xl border border-brand-border bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Lightbulb size={18} className="text-amber-500" />
            <h2 className="text-sm font-bold text-brand-ink">Also worth watching (not yet automated)</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {(report?.recommendations || []).map((r, i) => (
              <div key={i} className="rounded-xl bg-brand-surface p-3">
                <p className="text-sm font-semibold text-brand-ink">{r.title}</p>
                <p className="mt-0.5 text-xs text-brand-muted">{r.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
